'use client';

import { Trans, t } from '@lingui/macro';
import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Pagination,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { type FC, useCallback, useMemo, useState } from 'react';
import { Document, type DocumentProps, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { PoLoader } from '@prismo-io/design-system';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

type Props = {
  fileUrl: string;
};

type NonUndefined<T> = T extends undefined ? never : T;

type OnLoadSuccess = NonUndefined<DocumentProps['onLoadSuccess']>;
type OnLoadError = NonUndefined<DocumentProps['onLoadError']>;

export const RenderPdfFile: FC<Props> = (props) => {
  const { fileUrl } = props;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [numPages, setNumPages] = useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const onDocumentLoadSuccess: OnLoadSuccess = useCallback(
    ({ numPages = 1 }) => {
      setNumPages(numPages);
      setIsLoading(false);
    },
    []
  );

  const onDocumentLoadError: OnLoadError = useCallback(() => {
    setIsError(true);
    setIsLoading(false);
  }, []);

  return (
    <Stack alignItems="center" justifyContent="center">
      <Container maxWidth="md">
        <Paper
          elevation={2}
          sx={{
            margin: 'auto',
            width: 'fit-content',
            position: 'relative',
            '& > react-pdf__Document': {
              width: 'fit-content',
              '& > react-pdf__Page': {
                width: 'fit-content',
              },
            },
          }}
        >
          <Stack
            gap={1}
            alignItems="center"
            sx={{
              opacity: isLoading || isError ? 0 : 1,
              width: isLoading || isError ? 0 : 'auto',
              height: isLoading || isError ? 0 : 'auto',
              overflow: isLoading || isError ? 'hidden' : 'auto',
            }}
          >
            <Document
              file={fileUrl}
              externalLinkRel="nofollow"
              externalLinkTarget="_blank"
              loading={t`Chargement du document en cours...`}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
            >
              <Page pageNumber={pageNumber} />
            </Document>
            {numPages > 1 && (
              <Box component="div" paddingY={2}>
                <Pagination
                  page={pageNumber}
                  onChange={(_, page) => setPageNumber(page)}
                  count={numPages}
                />
              </Box>
            )}
          </Stack>
          {isLoading && (
            <Stack alignItems="center" gap={1} component="div" padding={2}>
              <PoLoader />
              <Typography>
                <Trans>Chargement du document en cours...</Trans>
              </Typography>
            </Stack>
          )}
          {isError && (
            <Stack alignItems="center" gap={1} component="div">
              <Alert severity="error">
                <AlertTitle>
                  <Trans>
                    Une erreur est survenue lors du chargement du document.
                  </Trans>
                </AlertTitle>
                <Trans>
                  Veuillez réessayer plus tard ou contactez nous pour plus
                  d'aide.
                </Trans>
              </Alert>
            </Stack>
          )}
        </Paper>
      </Container>
    </Stack>
  );
};
